import type { StorefrontSection } from '@mlm/shared';
import { useStorefrontContent } from '../../lib/hooks';
import { bannerStyle, hasBannerText } from '../../lib/banner-style';

type BannerGroupConfig = Extract<StorefrontSection, { type: 'bannerGroup' }>['config'];

// Static cols→class lookups; cols=3 default reproduces the former bottom-banners row.
const COLS_CLASS: Record<NonNullable<BannerGroupConfig['cols']>, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 lg:grid-cols-2',
  3: 'grid-cols-1 lg:grid-cols-3',
};

/**
 * Promo banner strip — names which CMS banner list to render. Reproduces the
 * former home bottom-banners block (hidden when the list is empty).
 */
export function BannerGroupSection({ section }: { section: StorefrontSection }) {
  const cfg = section.config as BannerGroupConfig;
  const cols = cfg.cols ?? 3;
  const data = useStorefrontContent().data;
  const banners = data[cfg.source] ?? [];
  if (banners.length === 0) return null;

  return (
    <section className={`mt-[18px] grid gap-3 ${COLS_CLASS[cols]}`}>
      {banners.map((b, i) => {
        // No caption ⇒ full-image banner: nothing drawn over it, no dark wash.
        const captioned = hasBannerText(b.title, b.sub);
        return (
          <div
            key={`${b.title}-${i}`}
            className="flex min-h-[120px] flex-col justify-center overflow-hidden rounded-lg p-[22px] text-white"
            style={bannerStyle({ imageKey: b.imageKey, gradient: b.gradient, dim: captioned })}
          >
            {b.title && <h3 className="text-[22px] font-extrabold leading-[1.05]">{b.title}</h3>}
            {b.sub && (
              <div className="mt-[5px] text-[12.5px] font-semibold opacity-90">{b.sub}</div>
            )}
          </div>
        );
      })}
    </section>
  );
}
